import { ProgramAssessmentFields } from "../../components/dtp/ProgramAssessmentFields";
import { trackStoreEvent } from "../../lib/dtpAnalytics";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type {
  PublicIntakeProgram,
  IntakeProgramSubmission,
} from "@bloom/shared";
import { dtpApi } from "../../lib/dtpApi";
import { dtpPath, useDtpParams } from "../../lib/clinicSite";
import { useDtpAuthStore } from "../../store/useDtpAuthStore";
import { useDtpCartStore } from "../../store/useDtpCartStore";
import { useQualificationDraft } from "../../hooks/useQualificationDraft";
import { programErrors } from "../../components/dtp/ProgramQuestions";

export function DtpProgramIntake() {
  const { physicianId, programId } = useDtpParams<{
    physicianId: string;
    programId: string;
  }>();
  const { patient, signupToken } = useDtpAuthStore();
  const { programs, setPrograms } = useQualificationDraft(
    JSON.stringify([physicianId, patient?._id || signupToken || "anonymous"]),
  );
  const { items, addItem, removeItem, applyClinicFromCatalog } =
    useDtpCartStore();
  const [program, setProgram] = useState<PublicIntakeProgram>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<any>();
  const navigate = useNavigate();
  const requestScope = JSON.stringify([
    physicianId,
    programId,
    patient?._id || signupToken || "anonymous",
  ]);
  const currentProgram = useRef(requestScope);
  currentProgram.current = requestScope;
  useEffect(() => {
    setResult(undefined);
    setError("");
  }, [requestScope]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setProgram(undefined);
    setResult(undefined);
    setError("");
    dtpApi
      .getCatalog(physicianId!)
      .then((res: any) => {
        if (!active) return;
        const flow = res.data?.clinic?.entryFlow;
        const p = res.data?.intakePrograms?.find(
          (p: PublicIntakeProgram) => p.id === programId && p.active,
        );
        if (
          !res.success ||
          !p ||
          flow?.mode === "direct" ||
          !flow?.programIds.includes(programId) ||
          res.data.clinic.isVet
        )
          throw new Error(
            "This guided program is unavailable. Contact your clinic.",
          );
        setProgram(p);
        applyClinicFromCatalog(res.data.clinic);
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [physicianId, programId, applyClinicFromCatalog]);
  const submission: IntakeProgramSubmission | undefined = program
    ? {
        programId: program.id,
        revision: program.revision,
        ...(programs[program.id]?.revision === program.revision
          ? programs[program.id]
          : {}),
        entry: "guided",
        answers:
          programs[program.id]?.revision === program.revision
            ? programs[program.id].answers
            : {},
      }
    : undefined;
  return (
    <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <Link className="text-blue-700 underline" to={dtpPath(physicianId)}>
        Back to store
      </Link>
      {loading && <p role="status">Loading assessment…</p>}
      {error && (
        <p role="alert" className="rounded-lg bg-red-50 p-4 text-red-700">
          {error}
        </p>
      )}
      {program && submission && (
        <fieldset disabled={loading} className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold">{program.name}</h1>
            <p className="mt-2 text-gray-600">{program.description}</p>
          </div>
          <ProgramAssessmentFields
            program={program}
            submission={submission}
            showState
            onChange={(updated) => {
              setPrograms((current) => ({
                ...current,
                [program.id]: updated,
              }));
              setResult(undefined);
              setError("");
            }}
          />
          <button
            disabled={loading}
            className="rounded-lg bg-blue-600 text-white px-5 py-3 disabled:opacity-50"
            onClick={async () => {
              const errors = programErrors(program, submission);
              if (errors.length) {
                setError(errors.join(". "));
                return;
              }
              setLoading(true);
              setError("");
              setResult(undefined);
              try {
                const response = await dtpApi.evaluateProgram(
                  physicianId!,
                  program.id,
                  submission,
                );
                if (currentProgram.current !== requestScope) return;
                if (!response.success) throw new Error(response.error);
                const selectedPlan = response.data?.decision?.plan;
                setPrograms((current) => ({
                  ...current,
                  [program.id]: { ...submission, planId: selectedPlan?.id },
                }));
                setResult(response.data);
              } catch (e: any) {
                if (currentProgram.current === requestScope)
                  setError(
                    e.response?.data?.error ||
                      e.message ||
                      "Unable to evaluate assessment",
                  );
              } finally {
                if (currentProgram.current === requestScope) setLoading(false);
              }
            }}
          >
            See my recommended program
          </button>
          {result && (
            <section
              className="rounded-xl border bg-white p-6 space-y-3"
              aria-label="Assessment result"
            >
              <h2 className="text-xl font-semibold">
                {result.decision.outcome === "review"
                  ? "Clinic review needed"
                  : result.decision.outcome === "questions"
                    ? "Complete your dosage plan assessment"
                    : "Your recommended option"}
              </h2>
              <p>{result.decision.message}</p>
              {result.decision.plan && (
                <p className="font-medium">
                  {result.decision.plan.name} · {result.decision.plan.doseLabel}
                </p>
              )}
              {result.decision.flags?.map(
                (flag: { ruleId: string; message: string }) => (
                  <p key={flag.ruleId} className="text-sm text-amber-800">
                    For your prescriber: {flag.message}
                  </p>
                ),
              )}
              {result.product && (
                <>
                  <p className="font-medium">
                    {result.product.displayName} · $
                    {result.product.price.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-600">
                    Your clinician confirms the final prescription. Continue to
                    complete any remaining required questions and review the
                    total.
                  </p>
                  {items.some((i) =>
                    program.productIds.includes(i.productId),
                  ) && (
                    <p className="text-sm text-gray-600">
                      This replaces your current selection for this program.
                    </p>
                  )}
                  <button
                    className="rounded-lg bg-blue-600 px-5 py-3 text-white"
                    onClick={() => {
                      items
                        .filter((i) => program.productIds.includes(i.productId))
                        .forEach((i) => removeItem(i.productId));
                      addItem({
                        productId: result.product._id,
                        productName: result.product.displayName,
                        price: result.product.price,
                      });
                      trackStoreEvent("add_to_cart", { productId: result.product._id, source: "guided-intake" });
                      navigate(dtpPath(physicianId, "/checkout"));
                    }}
                  >
                    Continue with this option
                  </button>
                </>
              )}
            </section>
          )}
        </fieldset>
      )}
    </main>
  );
}
